import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchData } from '../services/api';
import type { FinanceAccount, Transaction, Budget, TransactionCategory } from '@shared/types';

export default function Finances() {
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'budgets'>('overview');

  const { data: accounts, isLoading: loadingAccounts } = useQuery({
    queryKey: ['finance-accounts'],
    queryFn: () => fetchData<FinanceAccount[]>('/finances/accounts'),
  });

  const { data: recentTransactions, isLoading: loadingTransactions } = useQuery({
    queryKey: ['transactions', 'recent'],
    queryFn: () => fetchData<Transaction[]>('/finances/transactions?limit=10'),
  });

  const { data: budgets, isLoading: loadingBudgets } = useQuery({
    queryKey: ['budgets'],
    queryFn: () => fetchData<Budget[]>('/finances/budgets'),
  });

  const totalBalance = accounts?.reduce((sum, acc) => sum + acc.balance, 0) || 0;

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <header>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Finances</h1>
        <p className="text-neutral-500 dark:text-neutral-400 mt-1">Track your money</p>
      </header>

      {/* Total Balance Card */}
      <div className="card bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <p className="text-primary-200 text-sm">Total Balance</p>
        <p className="text-3xl font-bold mt-1">{formatCurrency(totalBalance)}</p>
        <div className="flex gap-4 mt-4">
          <button className="btn bg-white/20 hover:bg-white/30 text-white text-sm py-2">
            <PlusIcon />
            Add Account
          </button>
          <button className="btn bg-white/20 hover:bg-white/30 text-white text-sm py-2">
            <ExpenseIcon />
            Log Expense
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-neutral-200 dark:border-neutral-800">
        {(['overview', 'transactions', 'budgets'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500'
                : 'text-neutral-500 dark:text-neutral-400'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Accounts */}
          <section>
            <h2 className="text-lg font-semibold mb-3">Accounts</h2>
            {loadingAccounts ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="card flex items-center gap-3">
                    <div className="skeleton w-10 h-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="skeleton h-4 w-24" />
                      <div className="skeleton h-3 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : accounts && accounts.length > 0 ? (
              <div className="space-y-2">
                {accounts.map((account) => (
                  <AccountRow key={account.id} account={account} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="🏦"
                title="No accounts yet"
                description="Add your bank accounts to track balances"
                action="Add Account"
              />
            )}
          </section>

          {/* Child Support Tracking */}
          <section className="card child-related">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <span>👶</span>
                Child Support
              </h2>
              <span className="badge-warmth">Track</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-soft bg-warmth-50 dark:bg-warmth-900/20">
                <p className="text-sm text-warmth-600 dark:text-warmth-400">This Month</p>
                <p className="text-xl font-bold text-warmth-700 dark:text-warmth-300">$0.00</p>
              </div>
              <div className="p-4 rounded-soft bg-green-50 dark:bg-green-900/20">
                <p className="text-sm text-green-600 dark:text-green-400">YTD Total</p>
                <p className="text-xl font-bold text-green-700 dark:text-green-300">$0.00</p>
              </div>
            </div>
          </section>

          {/* Quick Stats */}
          <section>
            <h2 className="text-lg font-semibold mb-3">This Month</h2>
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Income"
                value={0}
                trend="up"
                color="green"
              />
              <StatCard
                label="Expenses"
                value={0}
                trend="down"
                color="red"
              />
            </div>
          </section>
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Recent Transactions</h2>
            <button className="text-sm text-primary-600 dark:text-primary-400">See All</button>
          </div>

          {loadingTransactions ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="card flex items-center gap-3">
                  <div className="skeleton w-10 h-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-4 w-32" />
                    <div className="skeleton h-3 w-20" />
                  </div>
                  <div className="skeleton h-5 w-16" />
                </div>
              ))}
            </div>
          ) : recentTransactions && recentTransactions.length > 0 ? (
            <div className="space-y-2">
              {recentTransactions.map((transaction) => (
                <TransactionRow key={transaction.id} transaction={transaction} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="📝"
              title="No transactions"
              description="Your transactions will appear here"
              action="Log Expense"
            />
          )}
        </div>
      )}

      {activeTab === 'budgets' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Budgets</h2>
            <button className="btn-primary text-sm py-2">
              <PlusIcon />
              Add Budget
            </button>
          </div>

          {loadingBudgets ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card space-y-3">
                  <div className="flex justify-between">
                    <div className="skeleton h-5 w-24" />
                    <div className="skeleton h-5 w-16" />
                  </div>
                  <div className="skeleton h-2 w-full rounded-full" />
                </div>
              ))}
            </div>
          ) : budgets && budgets.length > 0 ? (
            <div className="space-y-3">
              {budgets.map((budget) => (
                <BudgetCard key={budget.id} budget={budget} spent={0} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="📊"
              title="No budgets set"
              description="Create budgets to track your spending"
              action="Create Budget"
            />
          )}
        </div>
      )}
    </div>
  );
}

function AccountRow({ account }: { account: FinanceAccount }) {
  const typeIcons: Record<string, string> = {
    checking: '🏦',
    savings: '💰',
    credit: '💳',
    investment: '📈',
    loan: '📋',
    childSupport: '👶',
  };

  return (
    <div className="card-interactive flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-lg">
        {typeIcons[account.type] || '💵'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{account.name}</p>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {account.institution || account.type}
        </p>
      </div>
      <p className={`font-semibold tabular-nums ${
        account.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
      }`}>
        {formatCurrency(account.balance)}
      </p>
    </div>
  );
}

function TransactionRow({ transaction }: { transaction: Transaction }) {
  const categoryIcons: Record<TransactionCategory, string> = {
    childcare: '👶',
    education: '📚',
    medical: '🏥',
    childSupport: '👨‍👩‍👧',
    housing: '🏠',
    utilities: '💡',
    groceries: '🛒',
    transportation: '🚗',
    entertainment: '🎬',
    income: '💵',
    other: '📝',
  };

  const isChildRelated = ['childcare', 'education', 'medical', 'childSupport'].includes(transaction.category);

  return (
    <div className={`card-interactive flex items-center gap-3 ${isChildRelated ? 'child-related' : ''}`}>
      <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-lg">
        {categoryIcons[transaction.category]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{transaction.description}</p>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {new Date(transaction.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          {transaction.vendor && ` • ${transaction.vendor}`}
        </p>
      </div>
      <p className={`font-semibold tabular-nums ${
        transaction.type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-neutral-900 dark:text-neutral-100'
      }`}>
        {transaction.type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
      </p>
    </div>
  );
}

function BudgetCard({ budget, spent }: { budget: Budget; spent: number }) {
  const percentage = (spent / budget.amount) * 100;
  const isOverBudget = percentage > 100;
  const isNearLimit = percentage >= budget.alertThreshold;

  const categoryLabels: Record<TransactionCategory, string> = {
    childcare: 'Childcare',
    education: 'Education',
    medical: 'Medical',
    childSupport: 'Child Support',
    housing: 'Housing',
    utilities: 'Utilities',
    groceries: 'Groceries',
    transportation: 'Transportation',
    entertainment: 'Entertainment',
    income: 'Income',
    other: 'Other',
  };

  return (
    <div className={`card ${isOverBudget ? 'priority-urgent' : isNearLimit ? 'priority-high' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium">{categoryLabels[budget.category]}</span>
        <span className="text-sm text-neutral-500 dark:text-neutral-400">
          {formatCurrency(spent)} / {formatCurrency(budget.amount)}
        </span>
      </div>
      <div className="h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isOverBudget
              ? 'bg-red-500'
              : isNearLimit
              ? 'bg-warmth-500'
              : 'bg-green-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      {isOverBudget && (
        <p className="text-sm text-red-600 dark:text-red-400 mt-2">
          Over budget by {formatCurrency(spent - budget.amount)}
        </p>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  trend?: 'up' | 'down';
  color: 'green' | 'red';
}) {
  const colorClasses = {
    green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
  };

  return (
    <div className={`card ${colorClasses[color]}`}>
      <p className="text-sm opacity-80">{label}</p>
      <p className="text-xl font-bold mt-1">{formatCurrency(value)}</p>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: string;
  title: string;
  description: string;
  action: string;
}) {
  return (
    <div className="text-center py-8">
      <span className="text-4xl mb-4 block">{icon}</span>
      <h3 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">{title}</h3>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">{description}</p>
      <button className="btn-primary text-sm">{action}</button>
    </div>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function PlusIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function ExpenseIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}
